import { ReactElement, useMemo, useState } from 'react';
import { useInfiniteScrollSentinel } from '@/ui/hooks';
import { CompanyUser, CreateTeamUserRequest } from '@/api';
import { useCompanyContext } from '../providers';
import { useAddTeamUserAction } from '../actions';
import {
    UIModal,
    UIAvatar,
    UIButton,
    UIEmptyState,
    UIErrorState,
    UIFormInput,
    UILoadingState,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISpinner,
    UIText
} from '@/ui';
import { charcoal } from '@/ui/styles';
import { useFetchTeamFilterOptions } from '@/common/queries';
import { useDebouncedValue } from '@/common/hooks';
import { useCompaniesTranslation } from '../hooks';

type AddUserTeamsModalProps = {
    companyUser: CompanyUser;
    isOpen: boolean;
    onClose: () => void;
};

/**
 * Adds a user to a team, picking the team for a fixed user. The mirror of
 * `AddTeamUsersModal`, which picks users for a fixed team.
 */
export function AddUserTeamsModal(props: AddUserTeamsModalProps): ReactElement {
    const { isOpen, onClose } = props;

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen && <AddUserTeamsModal2 {...props} />}
        </UIModal>
    );
}

function AddUserTeamsModal2(props: AddUserTeamsModalProps): ReactElement {
    const { companyUser, isOpen, onClose } = props;

    const { company } = useCompanyContext();

    const { addTeamUser, loading } = useAddTeamUserAction();
    const [teamIdLoading, setTeamIdLoading] = useState<string | null>(null);

    function onSubmit(teamId: string, data: CreateTeamUserRequest) {
        setTeamIdLoading(teamId);
        addTeamUser(companyUser.id, data)
            .finally(() => {
                setTeamIdLoading(null);
            })
            .catch(() => {}); // Error handled in hook
    }

    const excludeTeamIds = useMemo(() => {
        return companyUser.teams.map((team) => team.teamId);
    }, [companyUser.teams]);

    return (
        <AddUserTeamsModalForm
            companyId={company.id}
            excludeTeamIds={excludeTeamIds}
            isOpen={isOpen}
            onClose={onClose}
            loading={loading}
            teamIdLoading={teamIdLoading}
            onSubmit={onSubmit}
        />
    );
}

type AddUserTeamsModalFormProps = {
    companyId: string;
    /** Teams the user is already in — filtered out of each loaded page. */
    excludeTeamIds: string[];
    isOpen: boolean;
    loading: boolean;
    teamIdLoading: string | null;
    onClose: () => void;
    onSubmit: (teamId: string, data: CreateTeamUserRequest) => void;
};

function AddUserTeamsModalForm(
    props: AddUserTeamsModalFormProps
): ReactElement {
    const { isOpen } = props;

    return <>{isOpen ? <FormContent {...props} /> : null}</>;
}

function FormContent(props: AddUserTeamsModalFormProps): ReactElement {
    const {
        companyId,
        excludeTeamIds,
        loading,
        teamIdLoading,
        onClose,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebouncedValue(searchQuery.trim());

    const {
        options,
        status,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch
    } = useFetchTeamFilterOptions({
        companyId,
        search: debouncedSearch,
        enabled: true
    });

    // Filtered per page rather than server-side, so a page can render fewer
    // rows than it holds. The user's own team list is complete, so the result
    // is still correct.
    const excluded = useMemo(() => new Set(excludeTeamIds), [excludeTeamIds]);
    const teams = useMemo(
        () => options.filter((option) => !excluded.has(option.id)),
        [options, excluded]
    );

    const { ref: bottomRef } = useInfiniteScrollSentinel({
        hasNextPage,
        isFetchingNextPage,
        onLoadMore: fetchNextPage
    });

    function clearSearch() {
        setSearchQuery('');
    }

    return (
        <>
            <UIModalHeader title={t('components.add_user_teams_modal.title')} />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0
                    }}
                >
                    <UIFormInput
                        value={searchQuery}
                        placeholder={t('shared.labels.search')}
                        height={20}
                        onChange={setSearchQuery}
                    />
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                            overflowY: 'auto',
                            maxHeight: 400,
                            paddingTop: 16
                        }}
                    >
                        {status === 'pending' && <UILoadingState />}
                        {status === 'error' && (
                            <UIErrorState
                                title={t('shared.errors.load_teams')}
                                onRetry={refetch}
                            />
                        )}
                        {status === 'success' &&
                            teams.map((team) => (
                                <div
                                    key={team.id}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 16
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 8
                                        }}
                                    >
                                        <UIAvatar
                                            img={team.image ?? ''}
                                            alt={team.name}
                                            size={44}
                                        />
                                        <UIText medium>{team.name}</UIText>
                                    </div>
                                    <UIButton
                                        variant="default"
                                        loading={
                                            loading && teamIdLoading === team.id
                                        }
                                        disabled={loading}
                                        onClick={() => {
                                            // Roles are set afterwards via
                                            // Update Role, matching the team
                                            // page.
                                            onSubmit(team.id, {
                                                teamId: team.id,
                                                roles: {}
                                            });
                                        }}
                                    >
                                        {t('shared.labels.add')}
                                    </UIButton>
                                </div>
                            ))}

                        {/* Only meaningful once there are no further pages —
                            an early page can be emptied entirely by the
                            exclusion filter while more teams are still to
                            come. */}
                        {status === 'success' &&
                            teams.length === 0 &&
                            !hasNextPage &&
                            (debouncedSearch ? (
                                <UIEmptyState
                                    description={t('shared.labels.no_results')}
                                    buttonText={t('shared.labels.clear_search')}
                                    emoji="😔"
                                    onClick={clearSearch}
                                />
                            ) : (
                                <UIEmptyState
                                    description={t(
                                        'components.add_user_teams_modal.already_in_every_team'
                                    )}
                                    emoji="😔"
                                />
                            ))}

                        {status === 'success' && hasNextPage && (
                            <div
                                ref={bottomRef}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: 8
                                }}
                            >
                                {isFetchingNextPage && (
                                    <UISpinner color={charcoal} />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        text: t('shared.labels.close'),
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}
